(() => {
    const limits = [0, 22.5, 45, 90, 225, 360, 720];
    const accents = ['#b68c6b', '#d9ad85', '#cdc7b5', '#aeb6bf', '#d6e1e9', '#a9b1ba', '#d79b69'];
    const properties = ['--rank-image', '--rank-size', '--rank-position', '--rank-finish', '--gem', '--glint', '--shade'];
    class OnyxRankUnlock extends HTMLElement {
      connectedCallback() {
        this.events?.abort();
        this.events = new AbortController();
        const signal = this.events.signal;
        customElements.whenDefined('onyx-study-rank').then(() => {
          if (!this.isConnected || signal.aborted) return;
          this.bind(signal);
        });
      }
      bind(signal) {
        this.rankRoot = this.closest('onyx-study-rank');
        this.labels = JSON.parse(this.querySelector('[data-unlock-labels]').textContent);
        this.dialog = this.querySelector('[data-unlock-dialog]');
        this.replay = this.querySelector('[data-unlock-replay]');
        this.animations = [];
        this.customer = null;
        this.lastRank = null;
        this.pendingRank = null;
        const launch = this.rankRoot.querySelector('[data-unlock-launch]');
        if (launch) {
          launch.hidden = false;
          launch.addEventListener('click', () => this.show(this.lastRank ?? 0, true), { signal });
        }
        this.replay.addEventListener('click', () => this.play(), { signal });
        this.querySelector('[data-unlock-close]').addEventListener('click', () => this.dialog.close(), { signal });
        this.querySelector('[data-unlock-continue]').addEventListener('click', () => this.finish(), { signal });
        this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal });
        this.dialog.addEventListener('close', () => {
          this.stopAnimations();
          document.documentElement.classList.remove('osr-unlock-open');
          if (this.restoreFocus?.isConnected) this.restoreFocus.focus({ preventScroll: true });
        }, { signal });
        document.addEventListener('visibilitychange', () => this.flush(), { signal });
        window.addEventListener('focus', () => this.flush(), { signal });
        document.addEventListener('close', () => queueMicrotask(() => { if (!signal.aborted) this.flush(); }), { capture: true, signal });
        this.unsubscribe?.();
        this.unsubscribe = window.OnyxStudyTime.subscribe(view => {
          if (!view.signedIn) { this.customer = null; this.lastRank = null; this.pendingRank = null; return; }
          const rank = Math.max(0, limits.findLastIndex(hours => view.totalMs / 3600000 >= hours));
          if (this.customer !== view.customerId || this.lastRank === null) {
            this.customer = view.customerId;
            this.lastRank = rank;
            this.pendingRank = null;
            return;
          }
          if (rank > this.lastRank) this.pendingRank = rank;
          if (rank < this.lastRank) this.pendingRank = null;
          this.lastRank = rank;
          this.flush();
        }, this.rankRoot.dataset.trackingUrl || '/apps/onyx-study');
      }
      flush() {
        if (this.pendingRank === null || this.dialog.open || document.visibilityState !== 'visible' || !document.hasFocus()) return;
        const other = [...document.querySelectorAll('dialog[open]')].some(dialog => dialog !== this.dialog && !dialog.matches('.osr__dialog'));
        if (other) return;
        const rank = this.pendingRank;
        this.pendingRank = null;
        this.show(rank, false);
      }
      show(rank, preview) {
        if (!Number.isInteger(rank) || rank < 0 || rank > 6 || (!preview && rank === 0)) return;
        this.restoreFocus = document.activeElement;
        const details = this.rankRoot.querySelector('.osr__dialog');
        if (details?.open) { details.close(); this.restoreFocus = this.rankRoot.querySelector('.osr__open'); }
        this.currentRank = rank;
        this.preview = preview;
        const tier = this.rankRoot.querySelectorAll('.osr__tiers li')[rank];
        for (const property of properties) this.dialog.style.setProperty(property, tier.style.getPropertyValue(property));
        this.dialog.style.setProperty('--unlock-accent', accents[rank]);
        const name = tier.querySelector('strong').textContent;
        this.dialog.setAttribute('aria-label', `${this.labels.eyebrow}: ${name}`);
        this.querySelector('[data-unlock-name]').textContent = name;
        this.querySelector('[data-unlock-level]').textContent = `${this.labels.level} ${String(rank + 1).padStart(2, '0')} ${this.labels.of} 07`;
        this.querySelector('[data-unlock-message]').textContent = rank === 6 ? this.labels.solid_message : this.labels.message;
        this.querySelector('[data-unlock-milestone]').textContent = `${limits[rank].toLocaleString()} ${this.labels.milestone}`;
        this.querySelector('[data-unlock-preview-note]').hidden = !preview;
        this.replay.hidden = !preview;
        if (!this.dialog.open) this.dialog.showModal();
        document.documentElement.classList.add('osr-unlock-open');
        this.play();
      }
      animate(element, frames, options) {
        const animation = element.animate(frames, { fill: 'backwards', ...options });
        this.animations.push(animation);
        return animation;
      }
      play() {
        this.stopAnimations();
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const panel = this.querySelector('.osr-unlock__panel');
        const spin = this.querySelector('[data-unlock-spin]');
        this.animate(panel, [{ opacity: 0, transform: 'translateY(12px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: reduce ? 120 : 350, easing: 'ease-out' });
        if (reduce) return;
        this.animate(spin, [
          { opacity: 0, transform: 'translateY(28px) rotate(-14deg) scale(.76)', offset: 0 },
          { opacity: 1, transform: 'translateY(-5px) rotate(4deg) scale(1.025)', offset: .65 },
          { opacity: 1, transform: 'translateY(0) rotate(0deg) scale(1)', offset: 1 }
        ], { duration: 1700, delay: 100, easing: 'cubic-bezier(.22,.75,.3,1)' });
        this.querySelectorAll('[data-unlock-reveal]').forEach((element, index) => {
          this.animate(element, [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: index === 0 ? 80 : 850 + index * 100, easing: 'ease-out' });
        });
        this.querySelectorAll('.osr-unlock__orbit').forEach((orbit, index) => {
          this.animate(orbit, [{ opacity: 0, scale: '.4', rotate: '-40deg' }, { opacity: index ? .35 : 1, scale: '1', rotate: '0deg' }], { duration: 1600, delay: 250 + index * 100, easing: 'cubic-bezier(.16,1,.3,1)' });
        });
        const shine = this.querySelector('.osr-unlock__shine');
        this.animate(shine, [{ opacity: 0 }, { opacity: .65, offset: .4 }, { opacity: 0 }], { duration: 800, delay: 1250, easing: 'ease-out' });
        this.querySelectorAll('.osr-unlock__spark').forEach((spark, index) => {
          const angle = index * 45;
          this.animate(spark, [{ opacity: 0, transform: `rotate(${angle}deg) translateY(-68px) scaleY(.25)` }, { opacity: .6, offset: .25 }, { opacity: 0, transform: `rotate(${angle}deg) translateY(-155px) scaleY(.5)` }], { duration: 900, delay: 900 + index * 35, easing: 'ease-out' });
        });
      }
      stopAnimations() {
        for (const animation of this.animations || []) animation.cancel();
        this.animations = [];
      }
      finish() {
        const source = this.querySelector('.osr-unlock__gem--front').getBoundingClientRect();
        const target = document.querySelector('[data-osr-header-gem]')?.getBoundingClientRect();
        const styles = properties.map(property => [property, this.dialog.style.getPropertyValue(property)]);
        this.dialog.close();
        if (!target || matchMedia('(prefers-reduced-motion: reduce)').matches || target.top < 0 || target.bottom > innerHeight || !source.width) return;
        const flight = document.createElement('span');
        flight.className = 'osr__gem osr-unlock__flight';
        flight.setAttribute('aria-hidden', 'true');
        for (const [property, value] of styles) flight.style.setProperty(property, value);
        Object.assign(flight.style, { left: `${source.left}px`, top: `${source.top}px`, width: `${source.width}px`, height: `${source.height}px` });
        document.body.append(flight);
        const motion = flight.animate([
          { transform: 'none', opacity: .9 },
          { transform: `translate(${target.left - source.left}px, ${target.top - source.top}px) scale(${target.width / source.width})`, opacity: .15 }
        ], { duration: 650, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' });
        flight.style.transformOrigin = 'top left';
        motion.finished.then(() => flight.remove(), () => flight.remove());
      }
      disconnectedCallback() {
        this.unsubscribe?.();
        this.events?.abort();
        this.stopAnimations();
        if (this.dialog?.open) { this.dialog.close(); document.documentElement.classList.remove('osr-unlock-open'); }
      }
    }
    if (!customElements.get('onyx-rank-reveal')) customElements.define('onyx-rank-reveal', OnyxRankUnlock);
  })();
